import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { useAdminHolidays } from "@/hooks/use-admin-queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import { todayIST } from "@/lib/timezone";
import type { HolidayScope } from "@/lib/types";

const SCOPES: { value: HolidayScope; label: string }[] = [
  { value: "all", label: "Everyone" },
  { value: "devotees", label: "Yoga & PDC" },
  { value: "employees", label: "Employees only" },
];

function monthKey(d: Date): string {
  return format(d, "yyyy-MM");
}

export default function HolidaysPage() {
  const [cursor, setCursor] = useState(() =>
    startOfMonth(parseISO(todayIST()))
  );
  const month = monthKey(cursor);
  const adminPassword = getAdminPassword();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useAdminHolidays(month);

  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const [appliesTo, setAppliesTo] = useState<HolidayScope>("all");

  const year = cursor.getFullYear();
  const monthIndex = cursor.getMonth();

  const create = useMutation({
    mutationFn: () =>
      adminApi.createHoliday(adminPassword!, { date, name: name.trim(), applies_to: appliesTo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "holidays", month] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "monthly"] });
      setDate("");
      setName("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteHoliday(adminPassword!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "holidays", month] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "monthly"] });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Holidays</h2>
          <p className="text-sm text-muted-foreground">
            Temple closed / no attendance expected — shows as{" "}
            <span className="font-semibold text-violet-700">H</span> instead of
            absent
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex - 1, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <p className="min-w-[140px] text-center font-medium">
            {format(cursor, "MMMM yyyy")}
          </p>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCursor(new Date(year, monthIndex + 1, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-4">
        <h3 className="mb-3 font-medium">Add holiday</h3>
        <form
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="h-date">Date</Label>
            <Input
              id="h-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="h-name">Name</Label>
            <Input
              id="h-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Janmashtami"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Applies to</Label>
            <Select
              value={appliesTo}
              onValueChange={(v) => setAppliesTo(v as HolidayScope)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              className="w-full"
              disabled={create.isPending || !date || !name.trim()}
            >
              {create.isPending ? "Adding…" : "Add holiday"}
            </Button>
          </div>
        </form>
        {create.isError && (
          <p className="mt-2 text-sm text-red-600">Could not add holiday.</p>
        )}
      </div>

      {isLoading && (
        <p className="text-muted-foreground">Loading holidays…</p>
      )}
      {error && (
        <p className="text-red-600">Could not load holidays.</p>
      )}

      <ul className="divide-y rounded-lg border bg-white">
        {(data ?? []).length === 0 && !isLoading && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">
            No holidays this month.
          </li>
        )}
        {(data ?? []).map((h) => (
          <li
            key={h.id}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <div>
              <p className="font-medium">{h.name}</p>
              <p className="text-sm text-muted-foreground">{h.date}</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {SCOPES.find((s) => s.value === h.applies_to)?.label ?? h.applies_to}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => remove.mutate(h.id)}
                disabled={remove.isPending}
                aria-label="Delete holiday"
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
